import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ShieldCheck, RefreshCw, Download, AlertTriangle, AlertOctagon, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, FileText, FileSpreadsheet, Users, Clock, Check, Undo2, History } from 'lucide-react';

const API = '/api/hr-audit';

const CHECK_META = [
 { key: 'vertrag_coverage', labelKey: 'hrAudit.check.vertrag', descKey: 'hrAudit.check.vertragDesc' },
 { key: 'stammdaten_vollstaendig', labelKey: 'hrAudit.check.stammdaten', descKey: 'hrAudit.check.stammdatenDesc' },
 { key: 'tages_lueckenlosigkeit', labelKey: 'hrAudit.check.tage', descKey: 'hrAudit.check.tageDesc' },
 { key: 'lohnzettel_coverage', labelKey: 'hrAudit.check.lohnzettel', descKey: 'hrAudit.check.lohnzettelDesc' },
 { key: 'portal_zugang_verifiziert', labelKey: 'hrAudit.check.portal', descKey: 'hrAudit.check.portalDesc' },
 { key: 'austritte_unvollstaendig', labelKey: 'hrAudit.check.austritte', descKey: 'hrAudit.check.austritteDesc' },
 { key: 'lohnkosten_unvollstaendig', labelKey: 'hrAudit.check.lohnkostenUnvoll', descKey: 'hrAudit.check.lohnkostenUnvollDesc' },
 { key: 'lohnkosten_phasen_luecke', labelKey: 'hrAudit.check.lohnkostenLuecke', descKey: 'hrAudit.check.lohnkostenLueckeDesc' },
 { key: 'planung_nicht_synchron', labelKey: 'hrAudit.check.planungSync', descKey: 'hrAudit.check.planungSyncDesc' },
 { key: 'urlaub_ueberplant', labelKey: 'hrAudit.check.urlaubUeberplant', descKey: 'hrAudit.check.urlaubUeberplantDesc' },
 { key: 'position_abweichung', labelKey: 'hrAudit.check.positionAbweichung', descKey: 'hrAudit.check.positionAbweichungDesc' },
];

function StatusPill({ status, t }) {
 const cfg = status === 'error'
 ? { bg: '#FEE2E2', fg: '#DC2626', Icon: AlertOctagon, labelKey: 'hrAudit.status.error' }
 : status === 'warn'
 ? { bg: '#FEF3C7', fg: '#D97706', Icon: AlertTriangle, labelKey: 'hrAudit.status.warn' }
 : { bg: '#DCFCE7', fg: '#16A34A', Icon: CheckCircle2, labelKey: 'hrAudit.status.ok' };
 const Icon = cfg.Icon;
 return (
 <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[11px] font-semibold"
 style={{ backgroundColor: cfg.bg, color: cfg.fg }}>
 <Icon size={12} />
 {t(cfg.labelKey)}
 </span>
 );
}

function formatCacheAge(seconds, t) {
 if (seconds == null) return '';
 if (seconds < 60) return t('hrAudit.cache.nowFresh');
 const min = Math.floor(seconds / 60);
 return t('hrAudit.cache.minAgo').replace('{min}', min);
}

function maName(r) {
 return [r.vorname, r.nachname].filter(Boolean).join(' ') || '—';
}

// ─── Bestätigungs-Aktion (#1228) ──────────────────────────────
function ActionHead({ t }) {
 return (
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>
 {t('hrAudit.col.aktion')}
 </th>
 );
}

function ActionCell({ r, act }) {
 const { checkKey, t, onConfirm, onUnconfirm, busy } = act;
 const isBusy = busy && busy.has(r.signatur);
 if (r.bestaetigt) {
 const who = r.bestaetigt_von_name || '—';
 const when = r.bestaetigt_am ? String(r.bestaetigt_am).slice(0, 16).replace('T', ' ') : '';
 return (
 <td className="py-2 px-3 text-right whitespace-nowrap">
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold mr-1.5"
 style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
 title={`${who}${when ? ' · ' + when : ''}`}>
 <Check size={12} /> {t('hrAudit.best.geprueft')}
 </span>
 <button type="button" onClick={() => onUnconfirm(r.signatur)} disabled={isBusy}
 className="inline-flex items-center px-1.5 py-1 rounded border hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-tertiary)' }}
 title={t('hrAudit.best.aufheben')}>
 <Undo2 size={12} />
 </button>
 </td>
 );
 }
 return (
 <td className="py-2 px-3 text-right whitespace-nowrap">
 <button type="button" onClick={() => onConfirm(checkKey, r)} disabled={isBusy}
 className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-medium hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--accent)' }}>
 <Check size={12} /> {t('hrAudit.best.bestaetigen')}
 </button>
 </td>
 );
}

// ─── Detail-Tabelle pro Check ─────────────────────────────────
function VertragTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.kategorie')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.eintritt')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.detail')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 {r.mitarbeiter_id ? (
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 ) : maName(r)}
 </td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{r.kategorie}</td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.eintritt || '—'}</td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>
 {r.kategorie === 'aktiv_ohne_vertrag' && t('hrAudit.detail.tageOhneVertrag').replace('{d}', r.tage_ohne_vertrag ?? '?')}
 {r.kategorie === 'phasen_ohne_vertrag' && `${r.ungedeckte_tage ?? 0} ${t('hrAudit.detail.tageUngedeckt')}`}
 {r.kategorie === 'ohne_eintritt' && t('hrAudit.detail.keinEintritt')}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function StammdatenTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.anzahlFehlend')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.fehlendeFelder')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 text-right font-semibold" style={{ color: r.severity === 'error' ? '#DC2626' : '#D97706' }}>
 {r.anzahl_fehlend}
 </td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>
 {(r.fehlende_felder || []).map((f, j) => (
 <span key={j} className="inline-block mr-1.5 mb-1 px-1.5 py-0.5 rounded text-[11px]"
 style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary)' }}>
 {f}
 </span>
 ))}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function TageTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.anzahlTage')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.aeltester')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.neuester')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.beispiele')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 text-right font-semibold" style={{ color: r.severity === 'error' ? '#DC2626' : '#D97706' }}>
 {r.anzahl_fehlende_tage}
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.aeltester_fehltag}</td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.neuester_fehltag}</td>
 <td className="py-2 px-3 font-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
 {(r.beispiel_tage || []).slice(0, 5).join(', ')}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function LohnzettelTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.anzahlMonate')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.aeltester')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.neuester')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.fehlendeMonate')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 text-right font-semibold" style={{ color: r.severity === 'error' ? '#DC2626' : '#D97706' }}>
 {r.anzahl_fehlende_monate}
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.aeltester_fehlmonat}</td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.neuester_fehlmonat}</td>
 <td className="py-2 px-3 font-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
 {(r.fehlende_monate || []).slice(0, 8).join(', ')}
 {(r.fehlende_monate || []).length > 8 && ` +${r.fehlende_monate.length - 8}`}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function PortalTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.problem')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.email')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.letzterLogin')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.tageSeit')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3">
 <span className="px-2 py-0.5 rounded text-[11px]"
 style={{ backgroundColor: r.severity === 'error' ? '#FEE2E2' : '#FEF3C7',
 color: r.severity === 'error' ? '#DC2626' : '#D97706' }}>
 {t('hrAudit.problem.' + r.problem_typ)}
 </span>
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.email || '—'}</td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>
 {r.letzter_login ? r.letzter_login.slice(0, 10) : '—'}
 </td>
 <td className="py-2 px-3 text-right" style={{ color: 'var(--text-secondary)' }}>{r.tage_seit_login ?? '—'}</td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function DetailTable({ checkKey, rows, t, viewMode, onConfirm, onUnconfirm, busy }) {
 if (!rows || rows.length === 0) {
 return (
 <div className="p-6 text-center" style={{ color: 'var(--text-tertiary)' }}>
 <CheckCircle2 size={24} className="mx-auto mb-2" style={{ color: '#16A34A' }} />
 <div className="text-[13px]">{t('hrAudit.noIssues')}</div>
 </div>
 );
 }
 const filtered = rows.filter(r => {
 if (viewMode === 'confirmed') return r.bestaetigt;
 if (viewMode === 'open') return !r.bestaetigt;
 return true;
 });
 if (filtered.length === 0) {
 const isConfirmedView = viewMode === 'confirmed';
 return (
 <div className="p-6 text-center" style={{ color: 'var(--text-tertiary)' }}>
 {isConfirmedView
 ? <History size={24} className="mx-auto mb-2" style={{ color: 'var(--text-muted, #9CA3AF)' }} />
 : <CheckCircle2 size={24} className="mx-auto mb-2" style={{ color: '#16A34A' }} />}
 <div className="text-[13px]">
 {isConfirmedView ? t('hrAudit.best.noneConfirmed') : t('hrAudit.best.allConfirmed')}
 </div>
 </div>
 );
 }
 const act = { checkKey, t, onConfirm, onUnconfirm, busy };
 if (checkKey === 'vertrag_coverage') return <VertragTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'stammdaten_vollstaendig') return <StammdatenTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'tages_lueckenlosigkeit') return <TageTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'lohnzettel_coverage') return <LohnzettelTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'portal_zugang_verifiziert') return <PortalTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'austritte_unvollstaendig') return <AustritteTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'lohnkosten_unvollstaendig') return <LohnkostenUnvollTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'lohnkosten_phasen_luecke') return <LohnkostenLueckeTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'planung_nicht_synchron') return <PlanungSyncTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'urlaub_ueberplant') return <UrlaubUeberplantTable rows={filtered} t={t} act={act} />;
 if (checkKey === 'position_abweichung') return <PositionAbweichungTable rows={filtered} t={t} act={act} />;
 return null;
}

// #1810 F3: dv_position hat seit dem Ticket keinen Leser mehr und kann still veralten.
function PositionAbweichungTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.dvPosition', 'Position im Vertrag')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.berufsbezeichnung', 'Berufsbezeichnung (Stammdaten)')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.vertrag', 'Vertrag')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{r.dv_position || '—'}</td>
 <td className="py-2 px-3" style={{ color: r.severity === 'error' ? '#DC2626' : 'var(--text-primary)' }}>
 {r.berufsbezeichnung || t('hrAudit.col.keineBerufsbezeichnung', 'nicht gepflegt')}
 </td>
 <td className="py-2 px-3" style={{ color: 'var(--text-tertiary)' }}>{r.vertrag_bezeichnung || '—'}</td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

// #1748: Urlaub überplant — geplante Urlaube über Saldo + zulässigem Vorgriff
function UrlaubUeberplantTable({ rows, t, act }) {
 const fmt = (n) => n == null ? '—' : Number(n).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.anspruch', 'Anspruch')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.saldoHeute', 'Saldo heute')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.geplant', 'Geplant')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.effVorgriff', 'Eff. Vorgriff')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ueberzogenUm', 'Überzogen um')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.prognoseUj', 'Prognose UJ-Ende')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.wartefrist', 'Wartefrist')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}?tab=urlaub`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.anspruch)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.saldo_heute)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.geplant)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.eff_vorgriff)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: r.severity === 'error' ? '#DC2626' : '#D97706' }}>{fmt(r.ueberzogen_um)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: Number(r.prognose_uj) < -0.01 ? '#DC2626' : 'var(--text-secondary)' }}>{fmt(r.prognose_uj)}</td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{r.wartefrist ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function LohnkostenUnvollTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.eintritt', 'Eintritt')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.rolle', 'Rolle')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}?tab=stammdaten`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.eintritt_datum || '—'}</td>
 <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{r.lager_rolle || '—'}</td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function LohnkostenLueckeTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.luecke', 'Lücke')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.tage', 'Tage')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}?tab=stammdaten`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>
 {(r.luecke_ab || '—')} – {(r.luecke_bis || '—')}
 </td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: '#D97706' }}>
 {r.luecke_tage ? String(r.luecke_tage).split(' ')[0] : '—'}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function PlanungSyncTable({ rows, t, act }) {
 const fmt = (n) => n == null ? '—' : `€ ${Math.round(Number(n)).toLocaleString('de-DE')}`;
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.monat', 'Monat')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.auto', 'Auto')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.override', 'Override')}</th>
 <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.delta', 'Delta')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.grund', 'Grund')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.jahr}-{String(r.monat).padStart(2, '0')}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.auto_betrag)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmt(r.override_betrag)}</td>
 <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: Math.abs(r.delta) > 500 ? '#DC2626' : '#D97706' }}>
 {r.delta > 0 ? '+' : ''}{fmt(r.delta)}
 </td>
 <td className="py-2 px-3 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{r.grund || '—'}</td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

function AustritteTable({ rows, t, act }) {
 return (
 <table className="w-full text-[13px]">
 <thead>
 <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.austritt', 'Austritt')}</th>
 <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.fehlt', 'Fehlt')}</th>
 <ActionHead t={t} />
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={i} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <td className="py-2 px-3">
 <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
 {maName(r)}
 </Link>
 </td>
 <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.austritt_datum || '—'}</td>
 <td className="py-2 px-3" style={{ color: '#DC2626' }}>
 {r.problem_typ === 'beides_fehlt' && t('hrAudit.austritte.beidesFehlt', 'Auflösungsart + Begründung')}
 {r.problem_typ === 'aufloesungsart_fehlt' && t('hrAudit.austritte.aufloesungsartFehlt', 'Auflösungsart')}
 {r.problem_typ === 'begruendung_fehlt' && t('hrAudit.austritte.begruendungFehlt', 'Begründung')}
 </td>
 <ActionCell r={r} act={act} />
 </tr>
 ))}
 </tbody>
 </table>
 );
}

// ─── Zeiterfassung-Compliance Sektion (#991) ─────────────────
function ZeiterfassungComplianceCard({ t }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [open, setOpen] = useState(false);

 const load = useCallback(() => {
  setLoading(true);
  fetch('/api/zeiterfassung/compliance/hr-audit-summary', { credentials: 'include' })
   .then(r => r.ok ? r.json() : { mitarbeiter: [] })
   .then(j => setData(j.mitarbeiter || []))
   .catch(() => setData([]))
   .finally(() => setLoading(false));
 }, []);
 useEffect(() => { load(); }, [load]);

 if (data === null) return null;

 const total = data.reduce((acc, r) => acc + (r.offen || 0), 0);
 const status = total === 0 ? 'ok' : (total >= 10 ? 'error' : 'warn');

 return (
  <div className="bg-surface-card border border-line rounded-card overflow-hidden mt-4" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
   <button
    onClick={() => setOpen(!open)}
    className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-inset,#F8F9FC)] text-left"
   >
    <div className="flex items-center gap-3 flex-1 min-w-0">
     {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
     <div className="min-w-0 flex-1">
      <div className="font-semibold text-[14px] flex items-center gap-2 flex-wrap">
       <Clock size={14} style={{ color: 'var(--accent)' }} />
       {t('hrAudit.zeiterfassung.title', 'Zeiterfassung-Dokumentation')}
       <StatusPill status={status} t={t} />
       <span className="text-[12px] font-normal" style={{ color: 'var(--text-tertiary)' }}>
        <Users size={12} className="inline mr-1" />
        {data.length} {t('hrAudit.rows')} · {total} {t('hrAudit.zeiterfassung.openWarnings', 'offene Warnungen')}
       </span>
      </div>
      <div className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
       {t('hrAudit.zeiterfassung.desc', 'MA mit unvollständig dokumentierter Arbeitszeit (letzte 30 Tage), unter Toleranz-Schwelle der Compliance-Regel (#991).')}
      </div>
     </div>
    </div>
    <button
     type="button"
     onClick={(e) => { e.stopPropagation(); load(); }}
     disabled={loading}
     className="text-[12px] px-2 py-1 rounded border hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
     style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
    >
     <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
    </button>
   </button>
   {open && (
    <div className="border-t overflow-x-auto" style={{ borderColor: 'var(--border-default, #E5E7EB)', backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
     {data.length === 0 ? (
      <div className="p-6 text-center text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
       {t('hrAudit.zeiterfassung.empty', 'Keine offenen Compliance-Warnungen — alle MA aktuell unter Schwelle.')}
      </div>
     ) : (
      <table className="w-full text-[13px]">
       <thead>
        <tr className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
         <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.col.ma')}</th>
         <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.zeiterfassung.col.offen', 'Offen')}</th>
         <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.zeiterfassung.col.erledigt', 'Erledigt')}</th>
         <th className="text-right py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.zeiterfassung.col.avgProzent', 'Ø Quote')}</th>
         <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.zeiterfassung.col.aeltester', 'Ältester')}</th>
         <th className="text-left py-2 px-3 font-medium" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.zeiterfassung.col.neuester', 'Neuester')}</th>
        </tr>
       </thead>
       <tbody>
        {data.map((r) => (
         <tr key={r.mitarbeiter_id} className="border-b" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
          <td className="py-2 px-3">
           <Link to={`/mitarbeiter/${r.mitarbeiter_id}`} className="hover:underline" style={{ color: 'var(--accent)' }}>
            {maName(r)}
           </Link>
          </td>
          <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: r.offen > 0 ? '#DC2626' : 'var(--text-secondary)' }}>{r.offen}</td>
          <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.erledigt}</td>
          <td className="py-2 px-3 text-right font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.avg_prozent_offen ?? '—'}%</td>
          <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.aeltester_offen || '—'}</td>
          <td className="py-2 px-3 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.neuester_offen || '—'}</td>
         </tr>
        ))}
       </tbody>
      </table>
     )}
    </div>
   )}
  </div>
 );
}

// ─── Haupt-Component ─────────────────────────────────────────
export default function HrAudit() {
 const { t } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [expanded, setExpanded] = useState(new Set());
 const [viewMode, setViewMode] = useState('open'); // 'open' | 'confirmed' | 'all'
 const [busy, setBusy] = useState(new Set());

 const loadData = useCallback(async (force = false) => {
 setLoading(true);
 setError(null);
 try {
 const url = force ? `${API}/run?force=1` : `${API}/run`;
 const res = await fetch(url, { credentials: 'include' });
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 const json = await res.json();
 setData(json);
 } catch (err) {
 setError(err.message);
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => { loadData(false); }, [loadData]);

 const toggleExpand = (key) => {
 setExpanded(prev => {
 const n = new Set(prev);
 if (n.has(key)) n.delete(key); else n.add(key);
 return n;
 });
 };

 const setBusyFor = (sig, on) => {
 setBusy(prev => {
 const n = new Set(prev);
 if (on) n.add(sig); else n.delete(sig);
 return n;
 });
 };

 const confirmRow = async (checkKey, row) => {
 if (!row?.signatur) return;
 setBusyFor(row.signatur, true);
 setError(null);
 try {
 const res = await fetch(`${API}/bestaetigen`, {
 method: 'POST',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ signatur: row.signatur, kategorie: checkKey, wert_snapshot: row })
 });
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 await loadData(false);
 } catch (err) {
 setError(err.message);
 } finally {
 setBusyFor(row.signatur, false);
 }
 };

 const unconfirmRow = async (signatur) => {
 if (!signatur) return;
 setBusyFor(signatur, true);
 setError(null);
 try {
 const res = await fetch(`${API}/bestaetigen/${encodeURIComponent(signatur)}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 await loadData(false);
 } catch (err) {
 setError(err.message);
 } finally {
 setBusyFor(signatur, false);
 }
 };

 const handleDownload = async (format) => {
 try {
 const inc = viewMode !== 'open' ? '?include_confirmed=1' : '';
 const res = await fetch(`${API}/export/${format}${inc}`, { credentials: 'include' });
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 const blob = await res.blob();
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `hr-audit_${new Date().toISOString().slice(0, 10)}.${format}`;
 a.click();
 URL.revokeObjectURL(url);
 } catch (err) {
 setError(err.message);
 }
 };

 const results = data?.results || {};
 const summary = data?.summary || {};
 const cache = data?.cache || {};

 return (
 <div className="p-6" style={{ color: 'var(--text-primary)' }}>
 {/* Header */}
 <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
 <div>
 <h1 className="text-[22px] font-bold flex items-center gap-2">
 <ShieldCheck size={22} style={{ color: 'var(--accent)' }} />
 {t('hrAudit.title')}
 </h1>
 <p className="text-[13px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
 {t('hrAudit.subtitle')}
 </p>
 </div>

 <div className="flex items-center gap-2">
 {cache.cache_ts && (
 <span className="text-[11px] px-2 py-1 rounded-lg" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-tertiary)' }}>
 {cache.from_cache ? t('hrAudit.cache.cached') : t('hrAudit.cache.fresh')} · {formatCacheAge(cache.cache_age_seconds, t)}
 </span>
 )}
 <button
 onClick={() => loadData(true)}
 disabled={loading}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium border hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
 >
 <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
 {t('hrAudit.reload')}
 </button>
 <button
 onClick={() => handleDownload('xlsx')}
 disabled={loading || !data}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium border hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
 >
 <FileSpreadsheet size={14} />
 Excel
 </button>
 <button
 onClick={() => handleDownload('pdf')}
 disabled={loading || !data}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium border hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary)' }}
 >
 <FileText size={14} />
 PDF
 </button>
 </div>
 </div>

 {/* Summary row */}
 {data && (
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.summary.errors')}</div>
 <AlertOctagon size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold" style={{ color: summary.total_errors > 0 ? '#DC2626' : 'var(--text-primary)' }}>
 {summary.total_errors ?? 0}
 </div>
 </div>
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.summary.warns')}</div>
 <AlertTriangle size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold" style={{ color: summary.total_warns > 0 ? '#D97706' : 'var(--text-primary)' }}>
 {summary.total_warns ?? 0}
 </div>
 </div>
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.summary.ok')}</div>
 <CheckCircle2 size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold" style={{ color: summary.total_ok > 0 ? '#16A34A' : 'var(--text-primary)' }}>
 {summary.total_ok ?? 0}
 </div>
 </div>
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.summary.rows')}</div>
 <Users size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold">{summary.total_rows ?? 0}</div>
 </div>
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.best.offen', 'Offen')}</div>
 <AlertCircle size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold" style={{ color: (summary.total_offen ?? 0) > 0 ? '#D97706' : 'var(--text-primary)' }}>
 {summary.total_offen ?? 0}
 </div>
 </div>
 <div className="bg-surface-card border border-line rounded-card p-4">
 <div className="flex items-start justify-between mb-2">
 <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{t('hrAudit.best.bestaetigt', 'Bestätigt')}</div>
 <Check size={16} style={{ color: 'var(--text-tertiary)' }} />
 </div>
 <div className="text-2xl font-bold" style={{ color: (summary.total_bestaetigt ?? 0) > 0 ? '#16A34A' : 'var(--text-primary)' }}>
 {summary.total_bestaetigt ?? 0}
 </div>
 </div>
 </div>
 )}

 {error && (
 <div className="p-3 mb-4 rounded-lg border text-[13px]" style={{ borderColor: '#FECACA', backgroundColor: '#FEE2E2', color: '#991B1B' }}>
 {error}
 </div>
 )}

 {loading && !data && (
 <div className="p-12 text-center" style={{ color: 'var(--text-tertiary)' }}>
 <RefreshCw size={24} className="mx-auto mb-2 animate-spin" />
 {t('hrAudit.loading')}
 </div>
 )}

 {/* View-Mode Filter (#1228) */}
 {data && (
 <div className="flex items-center gap-1 mb-4 p-[3px] rounded-[10px] border w-fit" style={{ backgroundColor: 'var(--bg-inset,#F8F9FC)', borderColor: 'var(--border-default, #E5E7EB)' }}>
 {[
 { key: 'open', label: t('hrAudit.view.open', 'Offen'), count: summary.total_offen },
 { key: 'confirmed', label: t('hrAudit.view.confirmed', 'Bestätigt'), count: summary.total_bestaetigt },
 { key: 'all', label: t('hrAudit.view.all', 'Alle'), count: null },
 ].map(v => {
 const active = viewMode === v.key;
 return (
 <button
 key={v.key}
 type="button"
 onClick={() => setViewMode(v.key)}
 className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[13px] font-medium ${active ? 'bg-surface-card shadow-sm' : ''}`}
 style={{ color: active ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
 >
 {v.key === 'confirmed' && <History size={13} />}
 {v.label}
 {v.count != null && <span className="text-[11px] font-mono opacity-70">{v.count}</span>}
 </button>
 );
 })}
 </div>
 )}

 {/* Check list */}
 <div className="bg-surface-card border border-line rounded-card overflow-hidden">
 <div className="divide-y" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 {CHECK_META.map(meta => {
 const res = results[meta.key];
 if (!res) return null;
 const isOpen = expanded.has(meta.key);
 const rows = res.rows || [];
 return (
 <div key={meta.key} style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
 <button
 onClick={() => toggleExpand(meta.key)}
 className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-inset,#F8F9FC)] text-left"
 >
 <div className="flex items-center gap-3 flex-1 min-w-0">
 {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
 <div className="min-w-0 flex-1">
 <div className="font-semibold text-[14px] flex items-center gap-2 flex-wrap">
 {t(meta.labelKey)}
 <StatusPill status={res.status} t={t} />
 <span className="text-[12px] font-normal" style={{ color: 'var(--text-tertiary)' }}>
 <Users size={12} className="inline mr-1" />
 {rows.length} {t('hrAudit.rows')}
 </span>
 {(res.bestaetigt_count ?? 0) > 0 && (
 <span className="text-[11px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg" style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}>
 <Check size={11} /> {res.bestaetigt_count}
 </span>
 )}
 {(res.offen_count ?? 0) > 0 && (
 <span className="text-[11px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
 <AlertCircle size={11} /> {res.offen_count}
 </span>
 )}
 </div>
 <div className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
 {t(meta.descKey)}
 </div>
 </div>
 </div>
 </button>
 {isOpen && (
 <div className="border-t overflow-x-auto" style={{ borderColor: 'var(--border-default, #E5E7EB)', backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
 <DetailTable checkKey={meta.key} rows={rows} t={t} viewMode={viewMode} onConfirm={confirmRow} onUnconfirm={unconfirmRow} busy={busy} />
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>

 {/* #991 Zeiterfassung Compliance */}
 <ZeiterfassungComplianceCard t={t} />
 </div>
 );
}
